/**
 * =============================================================================
 * NOTICE:
 * 
 * This module, along with its related sub-modules, is being developed and tested
 * in a central repository at https://github.com/palainteractive/web-geocomply-lib
 * 
 * IMPORTANT:
 * Any modifications directly to these source files will not be accepted unless 
 * they are submitted as a pull request, with adaquate documentation and associated 
 * tests, to the central repository:
 * 	https://github.com/palainteractive/web-geocomply-lib
 * 
 * By adhering to this process, we ensure that all changes are properly reviewed 
 * and integrated, maintaining the integrity and consistency of the codebase.
 * 
 * =============================================================================
 */

import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { ReplaySubject, Observable, Subject } from 'rxjs';
import { GeoComplyStatus, GeoComplyReasons, GeoComplyHint, GeoComplyAppStores, OobeeOptions } from 'src/app/interfaces/geo-interface';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { ConfigService } from '../config/config.service';
import { EventsService } from '../event/event.service';
import { UserSessionService } from '../user-session/user-session.service';
import { WebservicesService } from '../webservices/webservices.service';
import { ScriptService } from '../script/script.service';
import { GeoComplyOobeeService } from './geo-comply-oobee.service';
import { GeoComplyNDBSService } from './geo-comply-ndbs.service';

declare var window: any;
declare var GeoComply: any;

const coerceInt = (a: string | number) => ("string" === typeof a ? parseInt(a) : a);

interface GeocomplyCertificate {
    expires?: number;
    license?: string;
}

@Injectable({
    providedIn: 'root'
})

export class GeoComplyService {
    public customer?: CustomerModel;
    public customer$: ReplaySubject<CustomerModel> = new ReplaySubject(1);
    
    public geoComplyStatus: Subject<GeoComplyStatus>;
    public gc_installer_id: string;
    public gc_env_id: string;
    public isMobileWeb: boolean = false;

    /* external GeoComply APIs */
    private geoComplySdkWrapper: any;
    private mobileWebClient: any;
    /* */

    private reason?: string;
    private hint?: GeoComplyHint;

    private certificates: Record<string, GeocomplyCertificate> = {}

    private user_id?: string;
    private now_ts?: number;
    private latestResult?: object;
    private next_required?: number;

    protected initialized: boolean = false;
    protected installed: boolean = false;
    protected connected: boolean = false;

    /** FEATURE FLAGS: */
    protected FEATURE_GEOCOMPLY: boolean = false;
    protected FEATURE_GEOCOMPLY_USENODOWNLOAD: boolean = false;
    /**  */

    constructor(
        public scriptService: ScriptService,
        public config: ConfigService,
        public events: EventsService,
        public platform: Platform,
        public userSession: UserSessionService,
        private webservices: WebservicesService,
        private geoComplyOobee: GeoComplyOobeeService,
        private geoComplyNDBS: GeoComplyNDBSService,
    ) {
        this.checkPlatform();

        this.geoComplyStatus = new Subject<GeoComplyStatus>();

        this.config.isLoaded().then(() => {
            const configMap = this.config.getConfig();
            this.gc_installer_id = configMap.FEATURE_GEOCOMPLY_OPERATOR_ID;
            this.gc_env_id = configMap.FEATURE_GEOCOMPLY_ENV_ID;
            /** SET LOCAL FEATURE FLAGS: */
            this.FEATURE_GEOCOMPLY = this.config.isFeatureON( configMap, "FEATURE_GEOCOMPLY");
            this.FEATURE_GEOCOMPLY_USENODOWNLOAD = this.config.isFeatureON( configMap, "FEATURE_GEOCOMPLY_USENODOWNLOAD");
            /** */
        })


        this.userSession.getCustomer().subscribe((customer: CustomerModel) => {
            if( undefined === customer)
                return;
        
              if( !customer || !customer.customerId) 
                return;
              if(
                (
                  !this.customer
                  || customer.customerId != this.customer.customerId
                )
              ) {
                this.customer = customer;
                this.customer$.next( customer);
              }

        });
        this.userSession.refreshCustomer();
    }

    checkPlatform() {
        this.platform.ready().then(() => {
            if (this.platform.is('cordova') || this.platform.is('capacitor')) {
                this.geoComplySdkWrapper = window.cordova.plugins.geoComplySdk;
            } else if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)
                || (/Macintosh/.test(window.navigator.userAgent) && window.navigator.maxTouchPoints > 0)) {
                return this.isMobileWeb = true;
            }
            return this.isMobileWeb = false;
        });
    }

    protected setTimestamp() {
        this.now_ts = (new Date()).getTime();
    }

    protected getTimestamp() {
        return this.now_ts;
    }

    public isInstalled(): boolean {
        return this.installed;
    }

    public isConnected(): boolean {
        if( this.platform.is( 'cordova') || this.platform.is( 'capacitor')
            || this.FEATURE_GEOCOMPLY_USENODOWNLOAD) {
            return true;
        } else {
            return this.connected;      // connected flag only used in PLC and Oobee implementations
        }
    }

    public async initialize(clientapp: string): Promise<void> {
        if(this.initialized || !this.FEATURE_GEOCOMPLY) {
            return;
        }

        //
        await this.config.isLoaded();
        //
        if( this.platform.is( 'cordova') || this.platform.is( 'capacitor')) {
            this.initialized = true;
            this.connected = true;
            return;
        } else {
            if( this.FEATURE_GEOCOMPLY_USENODOWNLOAD) {
                /** NDBS module handles initialize and returns */
                try {
                    await this.geoComplyNDBS.initialize();
                    this.initialized = true;
                } catch( err) {
                    console.warn( 'GeoComply: '+err);
                    this.initialized = false;
                    throw new Error( 'GeoComply: '+err);
                }
                return;
            } else {
                if( this.isMobileWeb) {
                    await this.scriptService.injectLib( 'https://cdn.geocomply.com/oobee/v3.5/oobee.js', 'application/javascript');
                } else {
                    const FEATURE_GEOCOMPLY_CLIENT_VERSION: String = this.config.getConfig().FEATURE_GEOCOMPLY_CLIENT_VERSION;
		    if( "4.1.0.0" === FEATURE_GEOCOMPLY_CLIENT_VERSION) {
			// this fixes an installation edge condition where the CLIENT_VERSION has been upgraded but the 
			// client's localstorage still contains the previous version in localStorage.
			this.scriptService.destroyLib( `https://cdn.geocomply.com/plc/4.0.0.4/geocomply-client.min.js`);
		    }
                    await this.scriptService.injectLib( `https://cdn.geocomply.com/plc/${FEATURE_GEOCOMPLY_CLIENT_VERSION}/geocomply-client.min.js`, 'module');
                }
            }
        }

        this.connected = false;

        if( this.isMobileWeb) {
            this.initializeMobileWeb(clientapp);
            this.initialized = true;
            this.connected = true;
            return;
        }

        if ("undefined" === typeof this.certificates[clientapp]) {
            this.certificates[clientapp] = {
                expires: undefined,
                license: undefined
            }
        }

        var mythis = this;
        await new Promise<void>( async (resolve, reject) => {   
            window.GeoComply.Client.on('connect', () => {
                console.log('GeoComply: Client connected');
                mythis.connected = true;
                mythis.installed = true;
                resolve();
            })
                .on('error', (errorCode: any, errorMessage: string) => {
                    switch (parseInt(errorCode)) {
                        case window.GeoComply.Client.CLNT_ERROR_LOCAL_SERVICE_COMMUNICATION:
                            console.log('GeoComply:: ErrCode=[CLNT_ERROR_LOCAL_SERVICE_COMMUNICATION]; ErrMessage=[' + errorMessage + ']');
                            GeoComply.Client.connect(this.gc_installer_id, this.gc_env_id);
                            this.initialized = true;
                            reject('GeoComply:: ErrCode=[CLNT_ERROR_LOCAL_SERVICE_COMMUNICATION]; ErrMessage=[' + errorMessage + ']');
                            return;
                        case window.GeoComply.Client.CLNT_ERROR_REQUEST_GEOLOCATION_IN_PROGRESS:
                            console.warn('GeoComply:: geolocation in progress, waiting...');
                            return;
                        case window.GeoComply.Client.CLNT_ERROR_LOCAL_SERVICE_UNSUP_VER:
                            console.error('GeoComply:: Connection to GeoComply Client failed. Details: ErrCode=[CLNT_ERROR_LOCAL_SERVICE_UNSUP_VER]; ErrMessage=[' + errorMessage + ']');
                            this.installed = false;
                            this.geoComplyStatus.next({
                                error_code: 615,
                                error_message: errorMessage,
                            } as GeoComplyStatus);
                            return;
                        case window.GeoComply.Client.CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE:
                            console.log('GeoComply:: Connection to GeoComply Client failed. Details: ErrCode=[CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE]; ErrMessage=[' + errorMessage + ']');
                            this.installed = false;
                            reject('GeoComply:: Connection to GeoComply Client failed. Details: ErrCode=[CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE]; ErrMessage=[' + errorMessage + ']');
                            break;
                        case window.GeoComply.Client.CLNT_ERROR_TRANSACTION_TIMEOUT:
                            console.error('GeoComply:: Connection to GeoComply Client failed. Details: ErrCode=[CLNT_ERROR_TRANSACTION_TIMEOUT]; ErrMessage=[' + errorMessage + ']');
                            break;
                        // Missing required parameters
                        case window.GeoComply.Client.CLNT_ERROR_WRONG_OR_MISSING_PARAMETER:
                            console.error('GeoComply:: GeoLocation failed. Details: ErrCode=[CLNT_ERROR_WRONG_OR_MISSING_PARAMETER]; ErrMessage=[' + errorMessage + ']');
                            reject( 'GeoComply:: GeoLocation failed. Details: ErrCode=[CLNT_ERROR_WRONG_OR_MISSING_PARAMETER]; ErrMessage=[' + errorMessage + ']');
                            break;
                        case window.GeoComply.Client.CLNT_ERROR_LICENSE_EXPIRED:
                        case window.GeoComply.Client.CLNT_ERROR_INVALID_LICENSE_FORMAT:
                            console.error('GeoComply:: GeoLocation failed. Details: ErrCode=[CLNT_ERROR_LICENSE_EXPIRED,CLNT_ERROR_INVALID_LICENSE_FORMAT (' + errorCode + ')]; ErrMessage=[' + errorMessage + ']');
                            reject( 'CLNT_ERROR_LICENSE_EXPIRED,CLNT_ERROR_INVALID_LICENSE_FORMAT (' + errorCode + ')');
                            // this.certificates[clientapp].expires = undefined;
                            // this.triggerGeolocation(mythis.user_id, this.reason!);
                            return;
                        case window.GeoComply.Client.CLNT_ERROR_SERVER_COMMUNICATION:
                            console.error('GeoComply:: GeoLocation failed. Details: ErrCode=[CLNT_ERROR_SERVER_COMMUNICATION]; ErrMessage=[' + errorMessage + ']');
                            break;
                        default:
                            console.error('GeoComply:: UNKNOWN ERROR CODE. Details: ErrCode=[' + errorCode + ']; ErrMessage=[' + errorMessage + ']');
                            console.error('GeoComply:: UNKNOWN ERROR CODE. ignoring...');
                            reject( 'GeoComply:: UNKNOWN ERROR CODE. Details: ErrCode=[' + errorCode + ']; ErrMessage=[' + errorMessage + ']');
                            return;
                    }

                    const statusObj: GeoComplyStatus = {
                        error_code: parseInt(errorCode),
                        error_message: errorMessage,
                        error_details: '',
                        geolocate_in: 0,
                        hint: mythis.hint
                    }

                    mythis.geoComplyStatus.next(statusObj as GeoComplyStatus);
                })
                .on('geolocation', function (data: string) {
                    mythis.onGeoComplyPacket(data);
                }).on('log', function (l) {
                    console.log('GEOCOMPLY: ', l);
                });

            window.GeoComply.Client.connect(mythis.gc_installer_id, mythis.gc_env_id);
            this.initialized = true;
        });
    }

    protected initializeMobileWeb(clientapp: string) {
        this.geoComplyOobee.getGeoStatuses().subscribe((geoStatus) => {
            const hint: string = String(geoStatus.hint);
            //if (hint.indexOf("oobee://") > -1) return;
            this.geoComplyStatus.next(geoStatus)
        });
        return this.geoComplyOobee.initializeMobileWeb(clientapp)
    }

    public killConnection(): void {
        console.log('GeoComply:: killConnection()');
        if (this.platform.is('cordova')) {
            //Do Nothing
        } else if (this.isMobileWeb) {
            //Do Nothing
        } else {
            window.GeoComply.Client.off('connect');
            window.GeoComply.Client.off('error');
            window.GeoComply.Client.off('geolocation');
            window.GeoComply.Client.off('log');
            window.GeoComply.Client.disconnect();
        }

        this.initialized = false;
        this.connected = false;
    }

    public reconnect() {
        if (this.platform.is('cordova')) {
            console.log('GeoComply:: Cordova: reconnect(): NOT IMPLEMENTED');

        } else if (this.isMobileWeb) {
            console.log('GeoComply:: oobee: reconnect()');
            let clientapp = this.config.getClientApp()
            return this.geoComplyOobee.initializeMobileWeb(clientapp);
        } else {
            console.log('GeoComply:: web: reconnect()');
            window.GeoComply.Client.connect(this.gc_installer_id, this.gc_env_id);
        }
    }

    protected onGeoComplyPacket(data: string) {

        this.customer$.subscribe( customer => {
            this.webservices.post('geoComply/data', {
                customer_id: customer.customerId,
                session_token: this.userSession.getSessionToken(),
                enc_data: encodeURIComponent(data),
                clientapp: this.config.getClientApp()
            }).toPromise().then((response: PlatformResponse<any>) => {
                //console.log('GeoComply:: geoStatus: ' + JSON.stringify(response));
                this.events.publish('event:geoComply:dismiss');
                this.geoComplyStatus.next({
                    error_code: coerceInt(response.payload.error_code),
                    error_message: response.payload.error_message,
                    error_details: response.payload.error_details,
                    geolocate_in: coerceInt(response.payload.geolocate_in),
                    troubleshooter_message: response.payload.troubleshooter_message,
                    hint: this.hint
                } as GeoComplyStatus);
            }).catch((e: any) => {
                console.warn('GeoComply:: EXCEPTION: ', e);
            });
        });
    }

    protected triggerGeolocation(username: any, reason: string): void {
        let clientapp = this.config.getClientApp()

        var solution: string;
        if( !this.platform.is('cordova') && !this.platform.is( 'capacitor') 
        && this.FEATURE_GEOCOMPLY_USENODOWNLOAD) {
            solution = 'ndbs';
        }
        this.getGeoComplyLicense(clientapp, solution).then((license) => {
            const user_id = username;
            this.reason = reason;

            if (this.platform.is('cordova') || this.platform.is( 'capacitor')) {
                console.log('GeoComply:: requestCordovaGeoComply() <cordova>');
                this.requestCordovaGeoComply( user_id, reason, license);
            } else if( this.FEATURE_GEOCOMPLY_USENODOWNLOAD) {
                this.geoComplyNDBS.triggerGeoComply(  user_id, reason, license);

            } else if (this.isMobileWeb) {
                console.log('GeoComply:: requestMobileWebGeoComply() <oobee>');
                this.geoComplyOobee.triggerMobileGeoComply( user_id, reason, license, this.certificates[clientapp]);
            } else {
                //console.log('GeoComply:: GeoComply.Client.requestGeolocation() <browser plugin>',  user_id, reason);
                window.GeoComply.Client
                    .setLicense(license)
                    .setGeolocationReason(reason)
                    .setUserId( user_id);
                window.GeoComply.Client.requestGeolocation()
            }
        });
    }

    protected getGeoComplyLicense(clientapp: string, solution: string): Promise<any> {
        var licensePromise = new Promise<any>((resolve, error) => {
            if ("undefined" === typeof this.certificates[clientapp]) {
                this.certificates[clientapp] = {
                    expires: undefined,
                    license: undefined
                }
            }
            let now = (new Date()).getTime();
            if (!this.certificates[clientapp].expires || (now >= this.certificates[clientapp].expires!)) {
                let licenseParams:any = { clientapp: clientapp };
                if( solution) {
                    licenseParams.solution = solution;
                }
                this.webservices.get('geoComply/license', licenseParams).subscribe((resp: any) => {
                    if (resp.result) {
                        this.certificates[clientapp].license = resp.payload.certificate;
                        if ("undefined" !== typeof resp.payload.expires) {
                            this.certificates[clientapp].expires = resp.payload.expires * 1000;    // convert seconds to milliseconds
                            resolve(this.certificates[clientapp].license);
                        } else {
                            this.certificates[clientapp].expires = 0;        // expire immediately, certificate is no good
                            error('invalid expiry on certificate');
                        }
                    } else {
                        error('invalid response from license server');
                    }
                });
            } else {
                resolve(this.certificates[clientapp].license);
            }
        });
        return licensePromise;
    }

    protected requestMobileWebGeoComply(username: string, reason: string, license: string, isBackground: boolean) {
        var mythis = this;
        console.log('GeoComply:: requesting geo (MobileWeb)');
        if (reason === GeoComplyReasons.firstGeolocation) {
            this.events.publish('event:geoComply:present');
        }
        this.mobileWebClient.setUserId(username);
        this.mobileWebClient.setReason(reason);
        this.mobileWebClient.setLicense(license);
        if (this.platform.is('android')) {
            console.log('GeoComply:: ****** isBackground: ', isBackground);
            this.mobileWebClient.connect(
                {
                    timeout: 15000,
                    userAction: (typeof isBackground === 'undefined') || !isBackground,
                    appStoreURL: GeoComplyAppStores.android,
                }, function () {      //success callback
                    mythis.mobileWebClient.request();
                }, function () {                                 //error callback
                    // emit Oobee not installed
                    console.info('GeoComply:: OOBEE NOT INSTALLED');
                    mythis.geoComplyStatus.next({
                        error_code: 103,
                        error_message: 'Connection to Oobee failed. Please start Oobee and re-try.',
                        error_details: {},
                        geolocate_in: 0,
                        hint: mythis.hint
                    } as GeoComplyStatus);
                });
        } else if (this.platform.is('ios')) {
            if (this.mobileWebClient.hasRegisteredRecently()) {
                console.log('oobeelog: request()');
                this.mobileWebClient.request();
            } else {
                console.log('oobeelog: connect()');
                this.mobileWebClient.connect(
                    {
                        timeout: 15000,
                        userAction: true, // userAction
                        appStoreURL: GeoComplyAppStores.ios,
                    }, function () {      //success callback
                        console.log('oobeelog: request()');
                        mythis.mobileWebClient.request();
                    }, function () {                                 //error callback
                        // emit Oobee not installed
                        console.error('oobeelog: connect error');
                        mythis.geoComplyStatus.next({
                            error_code: 103,
                            error_message: 'Connection to Oobee failed. Please start Oobee and re-try.',
                            error_details: {},
                            geolocate_in: 0,
                            hint: mythis.hint
                        } as GeoComplyStatus);
                    });
            }
        } else {
            console.error('GeoComply:: ERROR: unrecognised platform: ', this.platform.platforms());
            this.mobileWebClient.request();
        }
    }

    protected recheckRequired(username: string): string {
        this.setTimestamp();
        const user_id = '' + this.customer.customerId;
        if ((!user_id || (username !==  user_id))
            || !this.latestResult
            || !this.next_required) {
            if (this.reason) {
                return this.reason;
            } else {
                return GeoComplyReasons.firstGeolocation;
            }
        } else if (!this.getTimestamp() || this.getTimestamp()! >= this.next_required) {
            return GeoComplyReasons.periodic;
        }
        return '';  // empty string is falsy
    }

   /**
    * handleGeolocationRequest
    * This method should be called to cause a geolocation check to be requested.
    */
    public handleGeolocationRequest(username: any, reasonOverride?: string) {
        this.triggerGeolocation(username, reasonOverride);
    }

    public getGeoComplyStatusObservable(): Observable<GeoComplyStatus> {
        return this.geoComplyStatus.asObservable();
    }


    /**
     * ------------------ cordova methods -----------------------
     */
    public requestRuntimePermissions(onSuccess, onError) {
        this.geoComplySdkWrapper.requestRuntimePermissions(
            function (success) {
                if (typeof (onSuccess) === 'function') {
                    onSuccess(success);
                }
            },
            function (error) {
                if (typeof (onError) === 'function') {
                    onError(error);
                }
            },
            function (logMessage) {
                console.log(logMessage);
            }, [{
                "request_precise_location_permission": true
            }]
        );
    }

    protected requestCordovaGeoComply(username: string, reason: string, license: string) {
        console.log('GeoComply:: requestCordovaGeoComply()');
        setTimeout(() => {
            var mythis = this;
            mythis.requestRuntimePermissions((success: any) => {
                console.info('GeoComply:: requestRuntimePermissions: ', success);
                mythis.geoComplySdkWrapper.requestGeolocation(
                    function (data:any) {
                        mythis.onGeoComplyPacket(data);
                    },
                    function (message:string) {
                        console.info('geocomply message callback: ' + JSON.stringify(message));
                        //                        mythis.geoComplyStatus.next({
                        //                            error_code: coerceInt(message.code),
                        //                            error_message: message.message,
                        //                            error_details: null,
                        //                            geolocate_in: 0,
                        //                            hint: null
                        //                        } as GeoComplyStatus);
                    },
                    function (logMessage:string) {
                        console.info('GeoComply:: ' + logMessage);
                    },
                    [
                        {
                            user_id: username,
                            reason: reason,
                            license: license
                        }
                    ]
                );
            }, (error: any) => {
                let message: string = "Can not do geolocation: " + error;
                console.info('GeoComply:: error: ' + message);
                mythis.geoComplyStatus.next({
                    error_code: error.code,
                    error_message: 'Permissions not granted to run Geolocation.',
                    error_details: '',
                    geolocate_in: 0,
                } as GeoComplyStatus);

            });
        }, 0);              // setTimeout 1
    }

}
